import React, { Fragment, useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Input,
    Spinner,
    Collapse,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
} from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import CardHeaderSpan from '../../../Utils/CommonComponents/CardHeaderSpan';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { toast } from 'react-toastify';
import { getLabel } from '../../../sharedFunctions/CommonLable';
import moment from 'moment';
import Loader from '../../../Layout/Loader';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux-toolkit/store';
import { setSelectedDate as setReduxSelectedDate } from '../../../redux-toolkit/positionSlice';

interface MeJWContainerProps {
    onTabChange?: (tab: 'JW' | 'Doctor' | 'Pharmacy' | 'Stockist' | 'NCA') => void;
    onRefreshCounts?: () => void;
    agendaCounts?: {
        jw: number;
        doctorReported: number;
        doctorTotal: number;
        pharmacyReported: number;
        pharmacyTotal: number;
        stockistReported: number;
        stockistTotal: number;
        nca: number;
    };
}

export default function MeJWContainer({ onTabChange, onRefreshCounts, agendaCounts }: MeJWContainerProps = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const reduxSelectedDate = useSelector((state: RootState) => state.position.selectedDate);
    const initialDate = location.state?.date || reduxSelectedDate || localStorage.getItem("selectedDate") || moment().format('YYYY-MM-DD');
    const [selectedDate, setSelectedDate] = useState<string>(initialDate);

    const [jwList, setJwList] = useState<any[]>([]);
    const [loadingJwData, setLoadingJwData] = useState<boolean>(false);
    const [submittingJwData, setSubmittingJwData] = useState<boolean>(false);

    // Track expanded card indices
    const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});

    const toggleCard = (index: number) => {
        setExpandedCards(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const fetchJointWorkData = async (targetDate: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        setLoadingJwData(true);
        try {
            const formattedDateParam = moment(targetDate).format('YYYY-MM-DD');
            const url = `${serverUrl}${API.getJointWork}`;
            const response = await getData(url, true, { date: formattedDateParam });
            console.log("Joint work API response:", response);
            if (response.status === 200 || response.data?.statusCode === 200) {
                let list: any[] = [];
                const d = response.data;
                const payload = d?.data || d;

                const dailycallsRaw = Array.isArray(payload?.dailycalls) ? payload.dailycalls : [];
                const dayplanRaw = Array.isArray(payload?.dayplan) ? payload.dayplan : [];

                const dailycallsFormatted = dailycallsRaw.map((item: any) => ({
                    ...item,
                    isDailyCall: true
                }));

                const dayplanFormatted = dayplanRaw
                    .filter((item: any) => `${item.DcrStatus ?? item.dcr_status ?? item.status ?? ''}`.toLowerCase() !== 'completed')
                    .map((item: any) => ({
                        ...item,
                        isDailyCall: false
                    }));

                if (payload?.dayplan !== undefined || payload?.dailycalls !== undefined) {
                    list = [...dayplanFormatted, ...dailycallsFormatted];
                } else if (Array.isArray(payload)) {
                    list = payload;
                } else if (Array.isArray(payload?.items)) {
                    list = payload.items;
                } else if (Array.isArray(payload?.data)) {
                    list = payload.data;
                } else if (payload && typeof payload === 'object' && !Array.isArray(payload) && (payload.DcrId || payload.dcr_id || payload.DayPlanId || payload.day_plan_id)) {
                    list = [payload];
                } else {
                    list = [];
                }

                // Initialize employee_feedback and outlet_feedback defaults if missing
                const formattedList = list.map(item => ({
                    ...item,
                    employee_feedback: Array.isArray(item.employee_feedback) && item.employee_feedback.length > 0
                        ? item.employee_feedback
                        : [{ Question: "Were you with the team member on the JFW call?", Answer: "Yes" }],
                    outlet_feedback: Array.isArray(item.outlet_feedback) ? item.outlet_feedback : []
                }));

                setJwList(formattedList);
            } else {
                setJwList([]);
            }
        } catch (error) {
            console.error("Error fetching joint work data:", error);
            setJwList([]);
        } finally {
            setLoadingJwData(false);
        }
    };

    useEffect(() => {
        const activeDate = location.state?.date || reduxSelectedDate || localStorage.getItem("selectedDate") || moment().format('YYYY-MM-DD');
        if (activeDate !== selectedDate) {
            setSelectedDate(activeDate);
        }
    }, [reduxSelectedDate, location.state?.date]);

    useEffect(() => {
        if (selectedDate) {
            if (selectedDate !== reduxSelectedDate) {
                dispatch(setReduxSelectedDate(selectedDate));
            }
            fetchJointWorkData(selectedDate);
        }
    }, [selectedDate]);

    const [submittingJwIndex, setSubmittingJwIndex] = useState<number | null>(null);

    const handleUpdateJointWork = async (targetIdx?: number) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        if (!jwList || jwList.length === 0) {
            toast.error("No joint work records to update");
            return;
        }

        const targetList = typeof targetIdx === 'number' ? [jwList[targetIdx]] : jwList;

        if (typeof targetIdx === 'number') {
            setSubmittingJwIndex(targetIdx);
        } else {
            setSubmittingJwData(true);
        }

        try {
            const url = `${serverUrl}${API.updateJointWork}`;

            const itemsPayload = targetList.map((item: any) => ({
                dcr_id: item.dcr_id ?? item.dcrId ?? item.DcrId ?? item.id ?? 0,
                dcr_status: "Reported",
                employee_feedback: item.employee_feedback || [],
                outlet_feedback: item.outlet_feedback || [],
                nca_comments: item.nca_comments ?? "",
                managers: String(item.managers ?? "")
            }));

            const payload = itemsPayload.length === 1 ? itemsPayload[0] : { items: itemsPayload };

            const response = await postData(url, true, payload);

            if (response.status === 200 || response.data?.statusCode === 200 || response.data?.status === 200 || response.data?.success) {
                toast.success(response.data?.message || "Joint work data updated successfully!");
                if (typeof targetIdx === 'number') {
                    setExpandedCards(prev => ({ ...prev, [targetIdx]: false }));
                } else {
                    setExpandedCards({});
                }
                await fetchJointWorkData(selectedDate);
                if (onRefreshCounts) onRefreshCounts();
            } else {
                toast.error(response.data?.message || response.data?.error || "Failed to update joint work data");
            }
        } catch (error: any) {
            console.error("Update joint work error:", error);
            toast.error(error?.message || "An error occurred while updating joint work data");
        } finally {
            setSubmittingJwData(false);
            setSubmittingJwIndex(null);
        }
    };

    const getStatusBadgeColor = (status: string) => {
        switch ((status || '').toLowerCase()) {
            case 'reported':
            case 'completed':
                return 'success';
            case 'awatingfeedback':
            case 'pending':
                return 'warning';
            case 'in_progress':
                return 'info';
            default:
                return 'primary';
        }
    };

    return (
        <Fragment>
            <Container fluid className="p-3">
                <Card className="border shadow-sm">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                        <CardHeaderSpan
                            headingClassName="pb-0 text-dark fw-bold"
                            back={false}
                            heading="Joint Work (JW)"
                            span={`Joint Work Records for ${moment(selectedDate).format('dddd, DD MMMM YYYY')}`}
                        />
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                color="dark"
                                outline
                                className="d-flex align-items-center gap-1 px-3 py-2 fw-semibold btn-back-to-me"
                                style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                                onClick={() => {
                                    dispatch(setReduxSelectedDate(selectedDate));
                                    navigate('/me', { state: { date: selectedDate } });
                                }}
                            >
                                <FeatherIconCom iconName="ArrowLeft" size={15} />
                                <span>Back to ME</span>
                            </Button>
                        </div>
                    </div>

                    {/* Agenda Tabs Navigation (Excluding Mtp_Deviation per instructions) */}
                    <div className="d-flex align-items-center gap-4 mt-3 border-bottom pb-2 flex-wrap px-3" style={{ fontSize: '0.95rem' }}>
                        <span
                            className="fw-bold text-primary border-bottom border-primary border-3 pb-2 mb-n2 cursor-pointer"
                            style={{ cursor: 'pointer' }}
                        >
                            Joint Working - {agendaCounts ? agendaCounts.jw : jwList.length}
                        </span>
                        <span
                            className="fw-bold text-muted cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (onTabChange) onTabChange('Doctor');
                                else navigate('/me/FW', { state: { date: selectedDate, filter: 'Doctor' } });
                            }}
                        >
                            Doctor {agendaCounts ? `- ${agendaCounts.doctorReported} / ${agendaCounts.doctorTotal}` : ''}
                        </span>
                        <span
                            className="fw-bold text-muted cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (onTabChange) onTabChange('Pharmacy');
                                else navigate('/me/FW', { state: { date: selectedDate, filter: 'Pharmacy' } });
                            }}
                        >
                            Pharmacy {agendaCounts ? `- ${agendaCounts.pharmacyReported} / ${agendaCounts.pharmacyTotal}` : ''}
                        </span>
                        <span
                            className="fw-bold text-muted cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (onTabChange) onTabChange('Stockist');
                                else navigate('/me/FW', { state: { date: selectedDate, filter: 'Stockist' } });
                            }}
                        >
                            Stockist {agendaCounts ? `- ${agendaCounts.stockistReported} / ${agendaCounts.stockistTotal}` : ''}
                        </span>
                        <span
                            className="fw-bold text-muted cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (onTabChange) onTabChange('NCA');
                                else navigate('/me/NCA', { state: { date: selectedDate } });
                            }}
                        >
                            {getLabel("NCA")} {agendaCounts ? `- ${agendaCounts.nca}` : ''}
                        </span>
                    </div>

                    <CardBody className="p-4" style={{ color: '#000000', backgroundColor: '#f8fafc' }}>
                        {loadingJwData ? (
                            <Loader height="250px" />
                        ) : jwList.length > 0 ? (
                            <Row className="g-3">
                                {jwList.map((item: any, idx: number) => {
                                    const dcrId = item.dcr_id ?? (idx + 1);
                                    const empName = item.mr_name || item.employee_details?.name || item.employee_details?.employee_name || item.EmployeeName || item.employee_name || item.mr_details?.name || 'N/A';
                                    const empDesignation = item.employee_details?.designation || item.mr_details?.designation || '';

                                    const outletName = item.outlet?.outlet_name || item.outlet_name || item.OutletName || item.outlet_details?.outlet_name || 'N/A';
                                    const outletType = item.outlet?.outlet_type || item.agendacontroltype || 'PHARMACY';
                                    const outletSpecialty = item.outlet?.speciality || item.outlet?.specialization || item.speciality || item.outlet?.outlet_type || 'Others';
                                    const outletPhone = item.outlet?.mobile || item.outlet?.phone || item.outlet_phone || item.mobile || item.employee_details?.phone || '';
                                    const outletCode = item.outlet?.outlet_code || '';
                                    const outletAddress = item.outlet?.outlet_address || '';
                                    const townName = item.outlet?.town_name || item.town_name || item.Stownname || item.stownname || item.TownName || item.outlet?.TownName || item.GeoTowns?.Stownname || item.outlet?.GeoTowns?.Stownname || item.outlet?.town || item.town || item.outlet?.city || item.city || '';
                                    const agendaType = item.agendacontroltype || 'FW';
                                    const dcrStatus = item.dcr_status || 'Reported';
                                    const isReported = `${dcrStatus}`.toLowerCase() === 'reported';
                                    const ncaComments = item.nca_comments || '';

                                    const isExpanded = !!expandedCards[idx];

                                    return (
                                        <Col xs={12} md={6} xl={6} key={idx}>
                                            <Card
                                                className="border shadow-sm h-100 mb-0"
                                                style={{
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    borderColor: '#d4e3fc',
                                                    backgroundColor: '#eef4ff',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                {/* Card Header matching 1st image */}
                                                <div className="p-3">
                                                    {/* Row 1: Outlet Name + Type Pill */}
                                                    <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                                                        <div
                                                            className="fw-bold text-dark text-truncate"
                                                            style={{ fontSize: '1.05rem', fontWeight: 700, color: '#111827', textTransform: 'uppercase' }}
                                                            title={outletName}
                                                        >
                                                            {outletName}
                                                        </div>
                                                        <span
                                                            className="badge text-uppercase px-3 py-1.5 fw-bold text-white"
                                                            style={{
                                                                backgroundColor: '#0d6efd',
                                                                borderRadius: '6px',
                                                                fontSize: '0.75rem',
                                                                letterSpacing: '0.5px'
                                                            }}
                                                        >
                                                            {outletType}
                                                        </span>
                                                    </div>

                                                    {/* Row 2: Specialty / Activity + Town */}
                                                    <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                                                        <div className="d-flex align-items-center gap-2 text-dark small fw-semibold">
                                                            <FeatherIconCom iconName="Activity" size={14} className="text-primary" style={{ marginRight: '2px' }} />
                                                            <span>{outletSpecialty}</span>
                                                        </div>
                                                        <span className="small fw-semibold text-dark text-truncate" title={townName || ''}>
                                                            {townName}
                                                        </span>
                                                    </div>

                                                    {/* Row 3: JW - Employee Name + Status Badge */}
                                                    <div className="d-flex justify-content-between align-items-center gap-2 pt-1">
                                                        <div className="d-flex align-items-center gap-2 text-dark small fw-semibold">
                                                            <span
                                                                className="d-inline-flex align-items-center justify-content-center"
                                                                style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#e2e8f0', marginRight: '2px' }}
                                                            >
                                                                <FeatherIconCom iconName="User" size={12} className="text-secondary" />
                                                            </span>
                                                            <span>JW - {empName}</span>
                                                        </div>
                                                        <span
                                                            className="badge fw-bold px-3 py-1.5 text-white"
                                                            style={{
                                                                backgroundColor: isReported ? '#48bb78' : '#65a30d',
                                                                borderRadius: '6px',
                                                                fontSize: '0.78rem'
                                                            }}
                                                        >
                                                            {dcrStatus || (isReported ? 'Reported' : 'AwaitingFeedback')}
                                                        </span>
                                                    </div>

                                                    {/* Button to open Modal */}
                                                    <div className="mt-3 pt-2 border-top" style={{ borderColor: 'rgba(13, 110, 253, 0.15)' }}>
                                                        <Button
                                                            color={isReported ? "primary" : "success"}
                                                            size="sm"
                                                            className="w-100 p-2 fw-bold text-white d-flex align-items-center justify-content-center gap-1 shadow-sm border-0"
                                                            style={{ borderRadius: '6px', fontSize: '0.82rem' }}
                                                            onClick={() => toggleCard(idx)}
                                                        >
                                                            <span>{isReported ? "View Joint Work Details" : "Report Joint Work Call"}</span>
                                                            <FeatherIconCom iconName="ExternalLink" size={14} />
                                                        </Button>
                                                    </div>
                                                </div>

                                                {/* Modal Popup for JW Details & Feedback */}
                                                <Modal isOpen={isExpanded} toggle={() => toggleCard(idx)} size="lg" centered scrollable>
                                                    <ModalHeader toggle={() => toggleCard(idx)} className="bg-light border-bottom">
                                                        <div>
                                                            <h5 className="fw-bold text-dark mb-0">{outletName}</h5>
                                                            <small className="text-muted">{outletType} • {townName} • JW - {empName}</small>
                                                        </div>
                                                    </ModalHeader>
                                                    <ModalBody className="p-4 bg-white" style={{ color: '#000000' }}>
                                                        {/* Employee Feedback */}
                                                        {item.employee_feedback && item.employee_feedback.length > 0 && (
                                                            <div className="mb-4">
                                                                <label className="form-label small fw-bold text-dark mb-2" style={{ color: '#000000' }}>
                                                                    <FeatherIconCom iconName="UserCheck" size={14} className="me-1 text-primary" />
                                                                    Employee Feedback
                                                                </label>
                                                                {item.employee_feedback.map((ef: any, efIdx: number) => (
                                                                    <div key={efIdx} className="border p-3 rounded mb-2 bg-light" style={{ borderColor: '#dddddd' }}>
                                                                        <div className="small fw-bold text-dark mb-2" style={{ color: '#000000' }}>{ef.Question || "Were you with the team member on the JFW call?"}</div>
                                                                        <Input
                                                                            type="select"
                                                                            value={ef.Answer || "Yes"}
                                                                            disabled={isReported}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                setJwList(prev => prev.map((it, i) => {
                                                                                    if (i !== idx) return it;
                                                                                    const updatedEf = [...it.employee_feedback];
                                                                                    updatedEf[efIdx] = { ...updatedEf[efIdx], Answer: val };
                                                                                    return { ...it, employee_feedback: updatedEf };
                                                                                }));
                                                                            }}
                                                                            className="bg-white border text-dark shadow-none py-2 px-3 fw-semibold"
                                                                            style={{ fontSize: '0.88rem', color: '#000000' }}
                                                                        >
                                                                            <option value="Yes">Yes</option>
                                                                            <option value="No">No</option>
                                                                        </Input>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Outlet Feedback */}
                                                        {item.outlet_feedback && item.outlet_feedback.length > 0 && (
                                                            <div className="mb-4">
                                                                <label className="form-label small fw-bold text-dark mb-2" style={{ color: '#000000' }}>
                                                                    <FeatherIconCom iconName="HelpCircle" size={14} className="me-1 text-info" />
                                                                    Outlet Feedback
                                                                </label>
                                                                {item.outlet_feedback.map((of: any, ofIdx: number) => {
                                                                    if (!of.Question) return null;
                                                                    const isManagerQuestion = (of.Question || '').toLowerCase().includes('manager working');
                                                                    const isDisabled = isManagerQuestion || isReported;

                                                                    let displayAnswer = '';
                                                                    if (Array.isArray(of.Answer)) {
                                                                        displayAnswer = of.Answer.map((ansId: any) => {
                                                                            const mgr = item.manager_details?.find((m: any) => String(m.employee_id) === String(ansId));
                                                                            return mgr ? `${mgr.name} (${mgr.designation || 'Manager'})` : ansId;
                                                                        }).join(', ');
                                                                    } else {
                                                                        displayAnswer = of.Answer || '';
                                                                    }

                                                                    return (
                                                                        <div key={ofIdx} className="border p-3 rounded mb-2 bg-light" style={{ borderColor: '#dddddd' }}>
                                                                            <div className="small fw-bold text-dark mb-2" style={{ color: '#000000' }}>{of.Question}</div>
                                                                            <Input
                                                                                type="text"
                                                                                value={displayAnswer}
                                                                                disabled={isDisabled}
                                                                                readOnly={isDisabled}
                                                                                onChange={(e) => {
                                                                                    if (isDisabled) return;
                                                                                    const val = e.target.value;
                                                                                    setJwList(prev => prev.map((it, i) => {
                                                                                        if (i !== idx) return it;
                                                                                        const updatedOf = [...it.outlet_feedback];
                                                                                        updatedOf[ofIdx] = { ...updatedOf[ofIdx], Answer: val };
                                                                                        return { ...it, outlet_feedback: updatedOf };
                                                                                    }));
                                                                                }}
                                                                                className={`${isDisabled ? 'bg-light fw-bold' : 'bg-white fw-semibold'} border text-dark shadow-none py-2 px-3`}
                                                                                style={{ fontSize: '0.88rem', color: '#000000', cursor: isDisabled ? 'not-allowed' : 'text' }}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}

                                                        {/* Comments / Feedback */}
                                                        <div className="mb-3">
                                                            <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>
                                                                <FeatherIconCom iconName="MessageSquare" size={14} className="me-1 text-primary" />
                                                                Comments / Feedback
                                                            </label>
                                                            <Input
                                                                type="textarea"
                                                                rows={3}
                                                                placeholder="Enter comments..."
                                                                value={ncaComments}
                                                                disabled={isReported}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    setJwList(prev => prev.map((it, i) => i === idx ? { ...it, nca_comments: val } : it));
                                                                }}
                                                                className="bg-white border text-dark shadow-none py-2 px-3 fw-semibold"
                                                                style={{ fontSize: '0.9rem', color: '#000000' }}
                                                            />
                                                        </div>

                                                        {/* Individual Card Submit Button */}
                                                        {!isReported && (
                                                            <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                                                                <Button
                                                                    color="success"
                                                                    className="px-4 py-2 fw-bold border-0 text-white d-flex align-items-center gap-2"
                                                                    disabled={submittingJwIndex === idx}
                                                                    style={{ backgroundColor: '#198754', fontSize: '0.9rem', borderRadius: '8px', cursor: 'pointer' }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleUpdateJointWork(idx);
                                                                    }}
                                                                >
                                                                    {submittingJwIndex === idx ? <Spinner size="sm" color="light" /> : (
                                                                        <>
                                                                            <FeatherIconCom iconName="CheckCircle" size={16} />
                                                                            <span>Submit JW Record</span>
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </ModalBody>
                                                </Modal>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        ) : (
                            <div className="text-center py-5" style={{ color: '#000000' }}>
                                <FeatherIconCom iconName="Users" size={48} className="mb-3 text-dark" />
                                <h5 className="fw-bold text-dark" style={{ color: '#000000' }}>No Joint Work Records Found</h5>
                                <p className="small mb-0 text-dark fw-semibold" style={{ color: '#000000' }}>
                                    No joint work data is available for {moment(selectedDate).format('DD MMM YYYY')}.
                                </p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </Fragment>
    );
}
